'use client'
import React, { useState, useEffect, useRef } from 'react'

import { ClassNameValue } from 'tailwind-merge'

import { useTranslation } from '@/hooks/client'
import { tw } from '@/lib/utils'

import { CircleProgress } from './circle-progress'
import { LogoSvg } from './logo-svg'

type CircleScoreFluidSize = 'xs' | 'sm' | 'mid' | 'md' | 'lg' | 'xl' | '2xl'

export interface CircleScoreFluidProps {
  size?: CircleScoreFluidSize
  amount: number
  progress: number
  className?: ClassNameValue
  logoClassName?: ClassNameValue
  scoreTextClassName?: ClassNameValue
  scoreNumberClassName?: ClassNameValue
  isStatic?: boolean
  isStartFromZero?: boolean
  origin: string
  LogoComponent?: React.FC<{ className?: string }>
}
export const CircleScoreFluid: React.FC<CircleScoreFluidProps> = ({
  size,
  progress,
  amount,
  className,
  logoClassName,
  scoreTextClassName,
  scoreNumberClassName,
  isStatic = false,
  isStartFromZero = false,
  LogoComponent = LogoSvg,
  ...props
}) => {
  const { t } = useTranslation()
  // Keep the real score in SSR HTML. The mount effect can still animate from
  // zero for people, but crawlers that do not execute JavaScript see the value.
  const [displayAmount, setDisplayAmount] = useState(() => amount)
  const animationRef = useRef<number | null>(null)
  const sectionRef = useRef<HTMLElement>(null)

  const runAnimation = React.useCallback(() => {
    if (isStatic) {
      setDisplayAmount(amount)
      return
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }
    const duration = 1000
    const startTime = performance.now()
    const easeInOut = (v: number) => (v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2)
    const animate = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1)
      setDisplayAmount(amount * easeInOut(p))
      if (p < 1) {
        animationRef.current = requestAnimationFrame(animate)
      }
    }
    setDisplayAmount(0)
    animationRef.current = requestAnimationFrame(animate)
  }, [isStatic, amount])

  // Animate on mount
  useEffect(() => {
    runAnimation()
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [runAnimation])

  // Re-animate when parent panel becomes visible
  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const panel = el.closest('[data-model-id]')
    if (!panel) return
    const handleVisible = () => runAnimation()
    panel.addEventListener('panel-visible', handleVisible)
    return () => panel.removeEventListener('panel-visible', handleVisible)
  }, [runAnimation])

  const sizeClasses: Record<CircleScoreFluidSize, string> = {
    xs: 'min-w-[34px]',
    sm: 'min-w-[48px]',
    mid: 'min-w-[66px]',
    md: 'min-w-[72px]',
    lg: 'min-w-[100px]',
    xl: 'min-w-[144px]',
    '2xl': 'w-[236px]',
  }

  const amountClasses = 'score-amount-fluid'
  const logoTextClasses = 'score-logo-text-fluid'
  const logoSvgClasses = 'score-logo-svg-fluid'
  const logoTextSpanClasses = 'score-logo-span-fluid'

  return (
    <section
      ref={sectionRef}
      className={tw(
        'score relative aspect-square max-w-full rounded-full bg-white',
        // m-auto: the ring is drawn at 95% of the box, so without auto margins the
        // absolutely positioned SVG sits top-left and the number reads off-centre.
        '[&>svg]:absolute [&>svg]:inset-0 [&>svg]:m-auto [&>svg]:block [&>svg]:h-full [&>svg]:w-full',
        size && sizeClasses[size],
        className
      )}
      {...props}
    >
      <CircleProgress size="95%" progress={progress} isStatic={isStatic} isStartFromZero={isStartFromZero} />
      <div className="info @container flex h-full w-full flex-col items-center justify-center">
        <div className={tw('logo-text z-0 flex items-center justify-center', logoTextClasses)}>
          <LogoComponent className={tw('logo', logoClassName, logoSvgClasses)} />
          <span
            className={tw('text-navy leading-none tracking-[-0.00456rem]', scoreTextClassName, logoTextSpanClasses)}
          >
            {t('compare.summarySection.scoreTitle', 'Score')}
          </span>
        </div>
        <div
          className={tw(
            'amount text-navy z-0 font-semibold tracking-[-0.015rem] not-italic',
            scoreNumberClassName,
            amountClasses
          )}
        >
          {displayAmount?.toFixed(1)}
        </div>
      </div>
    </section>
  )
}
